import { FC, Fragment, memo } from 'react';
import { Link } from 'react-router-dom';

import Badge from '@/components/elements/Badge';
import GradeIcon from '@/components/elements/Icon/Grade';
import TimeFrameIcon from '@/components/elements/Icon/TimeFrame';
import OrderDeadline from '@/components/elements/Text/OrderDeadline';
import { RaceProgram } from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { orderByOrderDeadline } from '@/lib/race';
import { getVelodromeByCode } from '@/lib/race/velodrome';
import { BetDataList } from '@/modelTypes';
import { raceSelector } from '@/selectors/race';

import {
  ContentWrapper,
  CurrentRaceListStyleProps,
  EmptyText,
  Icons,
  Lower,
  RaceNum,
  ReleaseStatus,
  Upper,
  VelodromeName,
  Wrapper,
} from './style';

interface CurrentRaceListProps extends CurrentRaceListStyleProps {
  raceProgramId?: RaceProgram['id'];
  racePrograms: RaceProgram[];
  betDataList: BetDataList;
}

const CurrentRaceList: FC<CurrentRaceListProps> = memo(
  ({ raceProgramId, racePrograms, betDataList, gap, gridTemplateColumns }) => {
    const releasedPrograms = racePrograms.filter(
      rp => !rp.isCanceled && !rp.isSalesSuspended,
    );

    const currentRace = (rp: RaceProgram) => {
      // 直近開催予定レースの抽出
      const heldRace = rp.races.find(race => raceSelector(race).isHeld);
      if (heldRace) {
        return heldRace;
      }

      // 最後に終了したレースの抽出
      const lastFinishedRace = [...rp.races]
        .reverse()
        .find(
          race =>
            raceSelector(race).marginedOrderDeadline.isBefore(dayjst()) &&
            !raceSelector(race).isCanceled,
        );
      if (lastFinishedRace) {
        return lastFinishedRace;
      }
      return null;
    };

    const existsInPanel = (rp: RaceProgram) => {
      const ids = rp.races.map(r => r.id);
      return Object.keys(betDataList).some(id => ids.includes(id));
    };

    if (!releasedPrograms.length) {
      return <EmptyText>本日の開催はありません</EmptyText>;
    }

    return (
      <Wrapper gap={gap} gridTemplateColumns={gridTemplateColumns}>
        {orderByOrderDeadline(releasedPrograms).map((rp, i) => (
          <Fragment key={`link-${rp.id}-${i}`}>
            {currentRace(rp) !== null && (
              <Link
                data-testid={`link-${rp.id}-${i}`}
                to={`/${
                  getVelodromeByCode(rp.velodromeCode).romanName
                }/race/${rp.heldDate.format('YYYY-MM-DD')}/${
                  currentRace(rp)!.raceNum
                }`}
              >
                <Badge
                  isShow={existsInPanel(rp)}
                  hasCount={false}
                  top="-5px"
                  right="-5px"
                >
                  <ContentWrapper
                    data-testid={`wrapper-${rp.id}-${i}`}
                    isSelected={raceProgramId === rp.id}
                  >
                    <Upper>
                      <VelodromeName data-testid={`velodrome-${rp.id}-${i}`}>
                        {getVelodromeByCode(rp.velodromeCode).name.slice(0, 3)}
                      </VelodromeName>
                      <Icons>
                        <GradeIcon gradeName={rp.gradeName} />
                        <TimeFrameIcon timeFrame={rp.timeFrame} />
                      </Icons>
                    </Upper>
                    <Lower data-testid={`race-num-${rp.id}-${i}`}>
                      {raceSelector(currentRace(rp)!).isHeld && (
                        <RaceNum>{currentRace(rp)!.raceNum}R</RaceNum>
                      )}
                      <ReleaseStatus>
                        <OrderDeadline
                          orderDeadline={
                            raceSelector(currentRace(rp)!).marginedOrderDeadline
                          }
                        />
                      </ReleaseStatus>
                    </Lower>
                  </ContentWrapper>
                </Badge>
              </Link>
            )}
          </Fragment>
        ))}
      </Wrapper>
    );
  },
);

export default CurrentRaceList;
